import type { Menu } from "@/contexts/SidebarContext";

export interface MenuUrlParts {
  pathname: string;
  search: string;
  hash: string;
}

export const parseMenuUrl = (value: string): MenuUrlParts => {
  const rawValue = String(value || "");
  const hashIndex = rawValue.indexOf("#");
  const beforeHash = hashIndex >= 0 ? rawValue.slice(0, hashIndex) : rawValue;
  const hash = hashIndex >= 0 ? rawValue.slice(hashIndex + 1) : "";
  const queryIndex = beforeHash.indexOf("?");
  const pathname = queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const search = queryIndex >= 0 ? beforeHash.slice(queryIndex + 1) : "";
  return { pathname: pathname || "/", search, hash };
};

const pathSegments = (pathname: string): string[] => pathname.split("/").filter(Boolean);

const pathnameMatches = (currentPathname: string, targetPathname: string): boolean => {
  const currentSegments = pathSegments(currentPathname);
  const targetSegments = pathSegments(targetPathname);
  if (targetSegments.length > currentSegments.length) return false;

  return targetSegments.every((segment, index) => segment.startsWith("[") || segment === currentSegments[index]);
};

export const menuPathMatches = (currentPath: string, menuPath?: string, allowHash = false): boolean => {
  if (!menuPath) return false;
  const current = parseMenuUrl(currentPath);
  const target = parseMenuUrl(menuPath);
  if (target.hash && current.hash !== target.hash) return false;
  if (!target.hash && current.hash && !allowHash) return false;
  return pathnameMatches(current.pathname, target.pathname);
};

export const isMenuActive = (currentPath: string, menuPath?: string, subMenu?: Menu[]): boolean => {
  if (subMenu?.some((item) => isMenuActive(currentPath, item.path, item.subMenu))) return true;
  return menuPathMatches(currentPath, menuPath, Boolean(subMenu?.length));
};

export const findMenuTrail = (menus: Menu[], currentPath: string): Menu[] => {
  for (const menu of menus) {
    if (menu.subMenu?.length) {
      const childTrail = findMenuTrail(menu.subMenu, currentPath);
      if (childTrail.length > 0) return [menu, ...childTrail];
    }
    if (menuPathMatches(currentPath, menu.path)) return [menu];
  }
  return [];
};
